/**
 * 🌪️ CREATURE PERSONALITY ENGINE
 * The beating heart of the creature personality system.
 * 
 * Created by Tom & Azura - January 14, 2026
 * ❤️⚡️❤️
 */

import type {
  Temperament,
  Mood,
  PersonalityState,
  ExpressionOutput,
  Corner,
  Stimulus,
  StimulusType,
  Vector3,
} from './types';

export class CreaturePersonality {
  private temperament: Temperament;
  private state: PersonalityState;
  private frameId: number | null = null;
  private lastTick: number = 0;
  private forcedMoodUntil: number = 0;
  private onStateChange?: (state: PersonalityState) => void;

  constructor(temperament: Temperament, onStateChange?: (state: PersonalityState) => void) {
    this.temperament = temperament;
    this.onStateChange = onStateChange;
    this.state = this.createInitialState();
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================

  private createInitialState(): PersonalityState {
    const initialMood = this.temperament.initialMood;
    return {
      mood: initialMood,
      previousMood: null,
      energy: this.temperament.energy.initial,
      corner: 'center',
      moodStartTime: Date.now(),
      moodTickCount: 0,
      expression: this.calculateExpression(initialMood, this.temperament.energy.initial, 'center'),
      bulge: { x: 50, y: 50, scale: 0, opacity: 0 },
      isRunning: false,
    };
  }

  // ============================================================
  // STATE MACHINE CORE
  // ============================================================

  /**
   * Get the next mood based on transition probabilities
   */
  private getNextMood(currentMood: Mood): Mood {
    const transitions = this.temperament.moodMatrix[currentMood];
    if (!transitions || transitions.length === 0) {
      return currentMood;
    }

    // Calculate total weight
    const totalWeight = transitions.reduce((sum, t) => sum + t.weight, 0);
    
    // Random selection based on weights
    let random = Math.random() * totalWeight;
    for (const transition of transitions) {
      random -= transition.weight;
      if (random <= 0) {
        return transition.mood;
      }
    }

    return transitions[transitions.length - 1].mood;
  }

  /**
   * Get duration for current mood
   */
  private getMoodDuration(mood: Mood): number {
    const config = this.temperament.moodDurations[mood];
    if (!config) return 1000; // Default 1 second
    return config.base + Math.random() * config.variance;
  }

  /**
   * Check mood constraints (min/max duration)
   */
  private checkMoodConstraints(mood: Mood, elapsed: number): { canExit: boolean; mustExit: boolean } {
    const constraints = this.temperament.moodConstraints?.[mood];
    if (!constraints) {
      return { canExit: true, mustExit: false };
    }

    const canExit = !constraints.minDuration || elapsed >= constraints.minDuration;
    const mustExit = !!constraints.maxDuration && elapsed >= constraints.maxDuration;

    return { canExit, mustExit };
  }

  /**
   * Get a random corner different from current
   */
  private getRandomCorner(current: Corner): Corner {
    const corners: Corner[] = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];
    const available = corners.filter(c => c !== current);
    return available[Math.floor(Math.random() * available.length)];
  }

  // ============================================================
  // EXPRESSION CALCULATION
  // ============================================================

  private calculateExpression(mood: Mood, energy: number, corner: Corner): ExpressionOutput {
    const baseExpression = this.temperament.expressions[mood];
    if (!baseExpression) {
      return {
        rotation: { x: 0, y: 0, z: 0 },
        translation: { x: 0, y: 0, z: 0 },
        scale: 1,
        transitionMs: 150,
        glowIntensity: 0,
        glowColor: 'transparent',
      };
    }

    const energyMultiplier = energy / this.temperament.energy.max;
    const cornerConfig = this.temperament.corners?.[corner];

    // Calculate jitter based on mood and energy
    const jitterAmount = baseExpression.jitter * (0.5 + energyMultiplier * 0.5);
    const jitter = () => (Math.random() - 0.5) * jitterAmount * 2;

    // Base rotation with corner offset and jitter
    const rotation: Vector3 = {
      x: baseExpression.rotation.x + (cornerConfig?.transform?.x || 0) + jitter(),
      y: baseExpression.rotation.y + (cornerConfig?.transform?.y || 0) + jitter(),
      z: baseExpression.rotation.z + (cornerConfig?.transform?.z || 0) + jitter(),
    };

    // Base translation with corner offset
    const translation: Vector3 = {
      x: baseExpression.translation.x + (cornerConfig?.translation?.x || 0),
      y: baseExpression.translation.y + (cornerConfig?.translation?.y || 0),
      z: baseExpression.translation.z + (cornerConfig?.translation?.z || 0),
    };

    // Scale varies with energy
    const scale = baseExpression.scale * (0.9 + energyMultiplier * 0.1);

    // Transition speed - faster when high energy
    const transitionMs = baseExpression.transitionMs * (1.5 - energyMultiplier * 0.5);

    // Glow intensity scales with energy
    const glowIntensity = baseExpression.glowIntensity * energyMultiplier;

    return {
      rotation,
      translation,
      scale,
      transitionMs,
      glowIntensity,
      glowColor: baseExpression.glowColor,
      cssClass: baseExpression.cssClass,
    };
  }

  /**
   * Add dynamic movement based on mood (called every frame)
   */
  private addDynamicMovement(expression: ExpressionOutput, mood: Mood, energy: number): ExpressionOutput {
    const now = Date.now();
    const energyMult = energy / this.temperament.energy.max;

    // Different movement patterns per mood type
    // This creates the "breathing", "twitching", "raging" feel
    const baseExpr = this.temperament.expressions[mood];
    if (!baseExpr) return expression;

    const jitter = baseExpr.jitter * energyMult;

    // Subtle continuous movement
    const breathe = Math.sin(now / 1000) * jitter * 0.5;
    const twitch = Math.sin(now / 200) * jitter;
    const shake = Math.sin(now / 50) * jitter * 2;

    // Apply based on mood intensity
    let movementMultiplier = 0;
    if (this.temperament.bouncingMoods?.includes(mood)) {
      movementMultiplier = 1; // Full chaos for bouncing moods
    } else if (jitter > 5) {
      movementMultiplier = 0.5; // Medium for jittery moods
    } else {
      movementMultiplier = 0.1; // Subtle for calm moods
    }

    return {
      ...expression,
      rotation: {
        x: expression.rotation.x + shake * movementMultiplier,
        y: expression.rotation.y + twitch * movementMultiplier * 0.5,
        z: expression.rotation.z + breathe + shake * movementMultiplier,
      },
      translation: {
        x: expression.translation.x + shake * movementMultiplier * 0.8,
        y: expression.translation.y + breathe * 0.5,
        z: expression.translation.z,
      },
    };
  }

  // ============================================================
  // ENERGY SYSTEM
  // ============================================================

  private updateEnergy(deltaSeconds: number): void {
    const mood = this.state.mood;
    const energyConfig = this.temperament.energy;
    
    // Check if resting
    const isResting = energyConfig.restingMoods.includes(mood);
    
    // Get drain rate for current mood
    const drainRate = energyConfig.moodDrain[mood] || 0;
    
    // Calculate energy change
    let energyDelta = -drainRate * deltaSeconds;
    if (isResting) {
      energyDelta += energyConfig.regenRate * deltaSeconds;
    }

    // Apply and clamp
    this.state.energy = Math.max(0, Math.min(energyConfig.max, this.state.energy + energyDelta));
  }

  // ============================================================
  // MAIN TICK LOOP
  // ============================================================

  private tick = (): void => {
    if (!this.state.isRunning) return;

    const now = Date.now();
    const deltaMs = now - this.lastTick;
    const deltaSeconds = deltaMs / 1000;
    this.lastTick = now;

    // Update energy
    this.updateEnergy(deltaSeconds);

    // Check for mood change
    const moodElapsed = now - this.state.moodStartTime;
    const moodDuration = this.getMoodDuration(this.state.mood);
    const { canExit, mustExit } = this.checkMoodConstraints(this.state.mood, moodElapsed);

    // Check forced mood
    const isForcedMood = now < this.forcedMoodUntil;

    // Should we change mood?
    if (!isForcedMood && (mustExit || (canExit && moodElapsed > moodDuration))) {
      let newMood = this.getNextMood(this.state.mood);

      // Energy constraints
      const energyConfig = this.temperament.energy;
      if (this.state.energy < energyConfig.thresholds.exhausted) {
        // Force into a resting mood
        const restingMoods = energyConfig.restingMoods;
        if (!restingMoods.includes(newMood)) {
          newMood = restingMoods[0] || this.temperament.initialMood;
        }
      }

      // Apply mood change
      if (newMood !== this.state.mood) {
        this.state.previousMood = this.state.mood;
        this.state.mood = newMood;
        this.state.moodStartTime = now;
        this.state.moodTickCount = 0;
      } else {
        this.state.moodTickCount++;
      }
    } else {
      this.state.moodTickCount++;
    }

    // Corner bouncing
    if (this.temperament.bouncingMoods?.includes(this.state.mood)) {
      const bounceInterval = this.temperament.bounceIntervals?.[this.state.mood] || 300;
      
      // Simple bounce timing based on tick count
      if (this.state.moodTickCount % Math.ceil(bounceInterval / 16) === 0) {
        const newCorner = this.getRandomCorner(this.state.corner);
        this.state.corner = newCorner;

        // Update bulge
        const cornerConfig = this.temperament.corners?.[newCorner];
        if (cornerConfig?.bulge) {
          this.state.bulge = {
            x: cornerConfig.bulge.x,
            y: cornerConfig.bulge.y,
            scale: 2,
            opacity: 1,
          };
        }
      }
    } else {
      this.state.corner = 'center';
    }

    // Fade bulge
    if (this.state.bulge.opacity > 0) {
      this.state.bulge = {
        ...this.state.bulge,
        scale: this.state.bulge.scale * 0.9,
        opacity: this.state.bulge.opacity * 0.85,
      };
    }

    // Calculate expression
    let expression = this.calculateExpression(this.state.mood, this.state.energy, this.state.corner);
    expression = this.addDynamicMovement(expression, this.state.mood, this.state.energy);
    this.state.expression = expression;

    // Notify listener
    this.onStateChange?.(this.state);

    // Schedule next frame
    this.frameId = requestAnimationFrame(this.tick);
  };

  // ============================================================
  // PUBLIC API
  // ============================================================

  start(): void {
    if (this.state.isRunning) return;
    this.state.isRunning = true;
    this.lastTick = Date.now();
    this.state.moodStartTime = Date.now();
    this.frameId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.state.isRunning = false;
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  reset(): void {
    this.stop();
    this.state = this.createInitialState();
    this.forcedMoodUntil = 0;
    this.onStateChange?.(this.state);
  }

  getState(): PersonalityState {
    return { ...this.state };
  }

  triggerStimulus(type: StimulusType, intensity: number = 1, data?: unknown): void {
    const handler = this.temperament.stimulusHandlers?.[type];
    if (!handler) return;

    const stimulus: Stimulus = { type, intensity, data };
    const response = handler(stimulus, this.state.mood, this.state.energy);

    // Handlers are responsible for returning null during uninterruptible states
    // (e.g., TassieDevil handlers return null during rage)
    if (response) {
      if (response.transitionTo) {
        this.state.previousMood = this.state.mood;
        this.state.mood = response.transitionTo;
        this.state.moodStartTime = Date.now();
        this.state.moodTickCount = 0;
      }

      if (response.energyDelta) {
        this.state.energy = Math.max(0, Math.min(
          this.temperament.energy.max,
          this.state.energy + response.energyDelta
        ));
      }

      if (response.forceDuration) {
        this.forcedMoodUntil = Date.now() + response.forceDuration;
      }

      this.onStateChange?.(this.state);
    }
  }

  forceMood(mood: Mood, duration?: number): void {
    if (!this.temperament.moods.includes(mood)) return;

    this.state.previousMood = this.state.mood;
    this.state.mood = mood;
    this.state.moodStartTime = Date.now();
    this.state.moodTickCount = 0;

    if (duration) {
      this.forcedMoodUntil = Date.now() + duration;
    }

    this.onStateChange?.(this.state);
  }

  getMoodIndicators(): { emoji: string; text: string; label: string } {
    const indicators = this.temperament.moodIndicators?.[this.state.mood];
    return {
      emoji: indicators?.emoji || '❓',
      text: indicators?.text || '???',
      label: indicators?.label || this.state.mood,
    };
  }
}
